import { Component, BaseOptions } from '../core/component';
import { Utils } from '../core/utils';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SliderOptions extends BaseOptions {}

const _defaults: SliderOptions = {};

// Inline values _set() replaced, per element, so destroy() can put back what
// an author wrote. Per element, not per instance: both handles of a range
// write the same host properties.
const _authored = new WeakMap<HTMLElement, Record<string, string>>();

export class Slider extends Component<SliderOptions> {
  declare el: HTMLInputElement;
  private _pointerDown: boolean;
  private _resizeObserver: ResizeObserver | null = null;
  value: HTMLElement;
  thumb: HTMLElement;

  constructor(el: HTMLInputElement, options: Partial<SliderOptions>) {
    super(el, options);

    this._pointerDown = false;
    this._setupThumb();
    this._sync();
    this._setupEventHandlers();
    this._resizeObserver = new ResizeObserver(() => this._sync());
    this._resizeObserver.observe(this.el);
    // The bubble and stop row are placed from the input's offsets, which
    // move when the host resizes around an input that keeps its size.
    if (this.el.parentElement) this._resizeObserver.observe(this.el.parentElement);
  }

  static get defaults(): SliderOptions {
    return _defaults;
  }

  static key = 'Expressive_Slider';

  destroy() {
    this._resizeObserver?.disconnect();
    this._removeThumb();
    super.destroy();
    const host = this._host();
    for (const el of [this.el, host]) {
      for (const name in _authored.get(el)) el.style.setProperty(name, _authored.get(el)[name]);
      _authored.delete(el);
    }
    // The other handle of a range writes the shared host properties again.
    host?.querySelectorAll('input[type="range"]').forEach((el) => el['Expressive_Slider']?._sync());
  }

  _set(el: HTMLElement, name: string, value: string) {
    const authored = _authored.get(el) ?? {};
    _authored.set(el, authored);
    authored[name] ??= el.style.getPropertyValue(name);
    el.style.setProperty(name, value);
  }

  // Eleven listeners used to cover four stages, because each one was written
  // twice - once for mouse, once for touch. One of the touch spellings,
  // `touchleave`, was never implemented by any browser at all.
  _setupEventHandlers() {
    const { signal } = this._events;
    this.el.addEventListener('change', this._handleRangeChange, { signal });
    this.el.addEventListener('pointerdown', this._handleRangePointerDown, { signal });
    // `input` alone tracks a drag. A `pointermove` sync used to run as well,
    // twice per frame, and before the browser had moved the value.
    this.el.addEventListener('input', this._handleRangeInput, { signal });
    this.el.addEventListener('pointerup', this._handleRangePointerUp, { signal });
    this.el.addEventListener('pointercancel', this._handleRangePointerUp, { signal });
    this.el.addEventListener('blur', this._handleRangeRelease, { signal });
    // `mouseout`, not `pointerout`: this used to be a `mouseout`/`touchleave`
    // pair, and `touchleave` was never implemented by any browser, so the
    // handler has only ever run for a mouse. `pointerout` also fires when a
    // touch lifts, which would clear the thumb's active state at a moment
    // nothing cleared it before.
    this.el.addEventListener('mouseout', this._handleRangeRelease, { signal });
    // A form reset restores the value without an input event.
    this.el.form?.addEventListener('reset', this._handleFormReset, { signal });
  }

  // The reset event fires before the browser restores the values.
  _handleFormReset = () => {
    setTimeout(() => {
      if (Slider.getInstance(this.el) === this) this._sync();
    });
  };

  _handleRangeChange = () => {
    this._clampDual();
    this._sync();
    this.thumb.classList.add('active');
  };

  _handleRangePointerDown = (e: PointerEvent) => {
    if (!e.isPrimary) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this._pointerDown = true;
    this.el.classList.add('active');
    this._clampDual();
    this._sync();
    // An `e.type !== 'input'` guard used to sit here. This handler is only ever
    // bound to a press, so it was never anything but true.
    this.thumb.classList.add('active');
  };

  _handleRangeInput = () => {
    this._clampDual();
    this._sync();
    this.thumb.classList.add('active');
  };

  _handleRangePointerUp = (e: PointerEvent) => {
    if (!e.isPrimary) return;
    this._pointerDown = false;
    this.el.classList.remove('active');
  };

  _handleRangeRelease = () => {
    if (!this._pointerDown) {
      this.thumb.classList.remove('active');
    }
  };

  _setupThumb() {
    this.thumb = document.createElement('span');
    this.value = document.createElement('span');
    this.thumb.classList.add('thumb');
    this.value.classList.add('value');
    this.thumb.append(this.value);
    this.el.after(this.thumb);
  }

  _removeThumb() {
    this.thumb.remove();
  }

  _isVertical(): boolean {
    return !!this.el.closest('.vertical');
  }

  _host(): HTMLElement | null {
    // `.slider` is the documented host now; without it here a dual-handle
    // control could not find its wrapper, so the handles never clamped and the
    // shared start/end fractions never tracked the interval.
    return this.el.closest('.slider, label');
  }

  _clampDual() {
    const host = this._host();
    const peers = host?.querySelectorAll('input[type="range"]');
    if (!peers || peers.length !== 2) return;
    const start = peers[0] as HTMLInputElement;
    const end = peers[1] as HTMLInputElement;
    if (this.el === start && +start.value > +end.value) start.value = end.value;
    if (this.el === end && +end.value < +start.value) end.value = start.value;
  }

  /** The input's range, defaulting like the browser does: 0 to 100. */
  private _bounds(el: HTMLInputElement) {
    const parsedMax = parseFloat(el.max);
    return { max: Number.isNaN(parsedMax) ? 100 : parsedMax, min: parseFloat(el.min) || 0 };
  }

  _fraction(el: HTMLInputElement): number {
    const { max, min } = this._bounds(el);
    const val = parseFloat(el.value) || 0;
    return max === min ? 0 : (val - min) / (max - min);
  }

  _sync() {
    const percent = this._fraction(this.el);
    const fraction = `${percent * 100}%`;

    // Read before writing. This runs on every pointer move during a drag, and
    // setting the custom property first made each of the four offset reads
    // below flush a fresh layout.
    const left = this.el.offsetLeft;
    const top = this.el.offsetTop;
    const width = this.el.offsetWidth;
    const height = this.el.offsetHeight;
    const style = getComputedStyle(this.el);
    const rtl = style.direction === 'rtl';
    const vertical = this._isVertical();
    const handle = parseFloat(style.getPropertyValue('--md-comp-slider-handle-width')) || 4;
    const length = vertical ? height : width;
    const position = handle / 2 + percent * (length - handle);

    this._set(this.el, '--md-comp-slider-active-fraction', fraction);
    this._set(this.el, '--md-comp-slider-active-position', `${position}px`);

    const host = this._host();
    const peers = host?.querySelectorAll('input[type="range"]');
    if (host && peers && peers.length === 2) {
      const nums = Array.from(peers).map((el) => this._fraction(el as HTMLInputElement));
      this._set(host, '--md-comp-slider-start-fraction', `${Math.min(...nums) * 100}%`);
      this._set(host, '--md-comp-slider-end-fraction', `${Math.max(...nums) * 100}%`);
      this._set(host, '--md-comp-slider-start-position', `${handle / 2 + Math.min(...nums) * (length - handle)}px`);
      this._set(host, '--md-comp-slider-end-position', `${handle / 2 + Math.max(...nums) * (length - handle)}px`);
    }
    if (host?.classList.contains('stops')) {
      const { max, min } = this._bounds(this.el);
      const step = parseFloat(this.el.step);
      // Stops sit at min + k * step up to max, so a step that does not divide
      // the range ends short of max. No step, `any`, zero or a negative step
      // clears the spacing, and the host paints no dots.
      const stops = step > 0 && Number.isFinite(step) && max > min;
      this._set(host, '--md-comp-slider-stop-count', stops ? String(Math.floor((max - min) / step + 1e-9) + 1) : '');
      this._set(host, '--md-comp-slider-stop-spacing', stops ? `${((length - handle) * step) / (max - min)}px` : '');
      // The tick row is the host's ::after, which cannot read the input's
      // properties. A dual host has no single position.
      if (peers?.length === 1) {
        this._set(host, '--md-comp-slider-active-position', `${position}px`);
        this._set(host, '--md-comp-slider-track-center', `${top + height / 2}px`);
      }
    }

    this.value.textContent = this.el.value;
    if (vertical) {
      this.thumb.style.left = `${left + width / 2}px`;
      this.thumb.style.top = `${top + height - position}px`;
    } else {
      this.thumb.style.left = `${left + (rtl ? width - position : position)}px`;
      this.thumb.style.top = `${top}px`;
    }
  }

  /**
   * Initializes every range input in the current document.
   */
  static Init() {
    // Deferred like every other Init(): this ran at import time, so a bundle
    // loaded in <head> found no inputs at all.
    Utils.onDocumentReady(() => {
      // An image compare's range is invisible and drawn by its container; a
      // color picker's hue range paints its own track.
      Slider.init(document.querySelectorAll<HTMLInputElement>('input[type=range]:not(.image-compare > input, .color-picker-hue)'));
    });
  }
}
